/**
 * Escapes special regex characters in a string
 */
const escapeRegex = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

interface TextNodeInfo {
  node: Text;
  start: number;
  end: number;
  text: string;
}

export const highlightTextInHtml = (
  htmlContent: string,
  searchTerm: string
): string => {
  if (!searchTerm || !htmlContent || searchTerm.trim() === "") {
    return htmlContent;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");

  const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodesInfo: TextNodeInfo[] = [];
  let flattenedText = "";

  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    const text = node.textContent || "";
    
    textNodesInfo.push({
      node,
      start: flattenedText.length,
      end: flattenedText.length + text.length,
      text,
    });
    
    flattenedText += text;
  }

  if (textNodesInfo.length === 0) {
    return htmlContent;
  }

  const searchRegex = new RegExp(escapeRegex(searchTerm), "gi");
  const matches: { start: number; end: number }[] = [];
  let match;

  while ((match = searchRegex.exec(flattenedText)) !== null) {
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  if (matches.length === 0) {
    return htmlContent;
  }

  // Process in reverse order to avoid index shifting issues
  for (let i = textNodesInfo.length - 1; i >= 0; i--) {
    const nodeInfo = textNodesInfo[i];
    const highlightRanges: { start: number; end: number }[] = [];

    // Find all matches that intersect with this text node
    for (const matchRange of matches) {
      // Check if match overlaps with this text node
      if (matchRange.start < nodeInfo.end && matchRange.end > nodeInfo.start) {
        // Calculate the local range within this text node
        const localStart = Math.max(0, matchRange.start - nodeInfo.start);
        const localEnd = Math.min(
          nodeInfo.text.length,
          matchRange.end - nodeInfo.start
        );
        
        highlightRanges.push({ start: localStart, end: localEnd });
      }
    }

    if (highlightRanges.length === 0) {
      continue;
    }

    // Merge overlapping ranges and sort
    const mergedRanges = mergeRanges(highlightRanges);

    // Create a fragment with highlighted content
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    for (const range of mergedRanges) {
      // Add text before the highlight
      if (range.start > lastIndex) {
        fragment.appendChild(
          document.createTextNode(nodeInfo.text.slice(lastIndex, range.start))
        );
      }

      // Add highlighted portion
      const mark = document.createElement("mark");
      mark.className = "search-highlight";
      mark.textContent = nodeInfo.text.slice(range.start, range.end);
      fragment.appendChild(mark);

      lastIndex = range.end;
    }

    // Add remaining text after the last highlight
    if (lastIndex < nodeInfo.text.length) {
      fragment.appendChild(
        document.createTextNode(nodeInfo.text.slice(lastIndex))
      );
    }

    // Replace the text node with the fragment
    nodeInfo.node.parentNode?.replaceChild(fragment, nodeInfo.node);
  }

  return doc.body.innerHTML;
};


function mergeRanges(
  ranges: { start: number; end: number }[]
): { start: number; end: number }[] {
  if (ranges.length === 0) return [];

  //Sort by start position
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number }[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];

    if (current.start <= last.end) {
      //Overlapping or adjacent - merge
      last.end = Math.max(last.end, current.end);
    } else {
      // Non-overlapping - add new range
      merged.push(current);
    }
  }

  return merged;
}

export const highlightTextInPlain = (
  text: string,
  searchTerm: string
): string => {
  if (!searchTerm || !text || searchTerm.trim() === "") {
    return text;
  }

  const searchRegex = new RegExp(`(${escapeRegex(searchTerm)})`, "gi");
  return text.replace(
    searchRegex,
    '<mark class="search-highlight">$1</mark>'
  );
};
